import { useEffect, useState } from 'react';
import type { UpdateStatus } from '@/platform';
import { useI18n } from '@/ui/i18n/I18nProvider';
import { useServices } from '@/ui/services/ServicesContext';
import styles from './UpdatePrompt.module.css';

/**
 * The installed app's "Có bản mới" (platform/native/updates.ts): checks on launch and on resume. A live
 * update downloads quietly and starts with the next launch, or now on "Cập nhật" (save flushed first, it is
 * never touched). Only an app shell change asks for the new APK.
 */
export function NativeUpdatePrompt() {
  const { t } = useI18n();
  const { platform, saves } = useServices();
  const [status, setStatus] = useState<UpdateStatus>({ kind: 'none' });
  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    if (platform.kind !== 'native') return;
    let cancelled = false;
    const check = () => {
      void platform.updates.check().then((next) => {
        if (!cancelled && next.kind !== 'none') setStatus(next);
      });
    };
    check();
    const off = platform.lifecycle.onResume(check);
    return () => {
      cancelled = true;
      off();
    };
  }, [platform]);

  if (status.kind === 'none' || dismissed) return null;
  const apk = status.kind === 'apk' ? status.url : null;
  return (
    <div className={styles.toast} role="status" data-testid="native-update">
      <span>{t(apk ? 'ui.update.apk_available' : 'ui.update.available')}</span>
      <button
        type="button"
        className={styles.button}
        onClick={() => {
          if (apk) {
            platform.browser.openExternal(apk);
            setDismissed(true);
          } else {
            void saves.flush().then(() => platform.updates.apply());
          }
        }}
      >
        {t(apk ? 'ui.update.apk_download' : 'ui.update.now')}
      </button>
      <button
        type="button"
        className={styles.later}
        onClick={() => {
          setDismissed(true);
        }}
      >
        {t('ui.update.later')}
      </button>
    </div>
  );
}
